import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { borderRadius, spacing, size, typography } from '@izettle/otto';
import { actionFocusMixin } from '../../mixins.js';
import { Icon } from '../icon/index.js';
import { SpinnerSimple } from '../spinner-simple/index.js';
import { ButtonColorsCss } from './button-colors.js';

const borderWidths = {
  primary: "",
  secondary: "1px",
  destructive: "1px",
  destructiveSolid: "1px",
  minor: "1px",
  inline: "",
  inlineDestructive: ""
};
const getDisabledCursor = _ref => {
  let {
    loading
  } = _ref;
  return loading ? "progress" : "not-allowed";
};
const getSizeStyle = _ref2 => {
  let {
    size: buttonSize = "regular",
    noPadding = false
  } = _ref2;
  switch (buttonSize) {
    case "large":
      return css`
        min-height: ${size.large};
        padding: 0 ${noPadding ? 0 : spacing.large};
        ${noPadding && `min-height: 0;`}
        ${typography.large}
        line-height: 22px;
      `;
    // eslint-disable-next-line default-case-last
    default:
    case "regular":
      return css`
        min-height: ${size.medium};
        padding: 0 ${noPadding ? 0 : spacing.medium};
        ${noPadding && `min-height: 0;`}
        ${typography.regular}
      `;
    case "small":
      return css`
        min-height: ${size.xMedium};
        padding: 0 ${noPadding ? 0 : spacing.small};
        ${noPadding && `min-height: 0;`}
        ${typography.regular}
      `;
  }
};
const getButtonColors = _ref3 => {
  let {
    variant = "primary"
  } = _ref3;
  return css`
  ${ButtonColorsCss[variant]};
`;
};
const getBorder = _ref4 => {
  let {
    variant = "primary"
  } = _ref4;
  return borderWidths[variant] ? css`
        &:after {
          content: "";
          position: absolute;
          height: 100%;
          width: 100%;
          box-sizing: border-box;
          border-color: inherit;
          border-style: solid;
          border-width: ${borderWidths[variant]};
          border-radius: inherit;
          pointer-events: none;
        }
      ` : "";
};
const getWidth = _ref5 => {
  let {
    fullWidth
  } = _ref5;
  if (!fullWidth) {
    return "";
  }
  return typeof fullWidth !== "number" ? css`
        width: 100%;
      ` : css`
        @media (max-width: ${fullWidth}px) {
          width: 100%;
        }
      `;
};
const getInlineUnderline = _ref6 => {
  let {
    variant
  } = _ref6;
  const underline = css`
    text-decoration-line: underline;
    text-underline-offset: 0.0625rem;
  `;
  switch (variant) {
    case "inline":
      return underline;
    case "inlineDestructive":
      return underline;
    default:
      return "";
  }
};
const baseStyles = css`
  border: 0;

  ${getWidth};
  ${getSizeStyle};
  ${getBorder};
  ${getButtonColors};
  ${getInlineUnderline};

  border-radius: ${borderRadius.medium};

  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;

  transition-property: color, background-color, border-color;
  transition-duration: 0.15s;
  transition-timing-function: ease;

  &:active {
    transition-duration: 0s;
  }

  &:focus {
    ${actionFocusMixin};
  }
`;

// Button components
const StyledButton = /*#__PURE__*/styled.button.attrs(_ref7 => {
  let {
    type = "button",
    disabled,
    loading
  } = _ref7;
  return {
    type,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    disabled: disabled || loading,
    loading: null // Omitting this prop to not confuse the native react button component
  };
}).withConfig({
  displayName: "styles__StyledButton",
  componentId: "zettle__sc-1wkicws-0"
})(["&&&{height:unset;user-select:none;cursor:pointer;", ";&:disabled{cursor:", ";}}"], baseStyles, getDisabledCursor);
const StyledLink = /*#__PURE__*/styled(Link).withConfig({
  displayName: "styles__StyledLink",
  componentId: "zettle__sc-1wkicws-1"
})(["text-decoration:none;", ""], baseStyles);
const StyledAnchor = /*#__PURE__*/styled.a.withConfig({
  displayName: "styles__StyledAnchor",
  componentId: "zettle__sc-1wkicws-2"
})(["text-decoration:none;", ""], baseStyles);

// Icon and loader
const getIconOuterMargin = p => spacing[p.buttonSize === "small" ? "tiny" : "xxSmall"];
const iconPositions = {
  right: css`
    padding-left: ${spacing.xxSmall};
  `,
  left: css`
    order: -1;
    /* stylelint-disable-next-line @izettle/spacing */
    padding-right: ${spacing.xxSmall};
  `
};
const iconVerticalAlignments = {
  top: css`
    align-self: start;
  `,
  center: css`
    align-self: center;
  `,
  bottom: css`
    align-self: end;
  `
};
const StyledSpinner = /*#__PURE__*/styled(SpinnerSimple).withConfig({
  displayName: "styles__StyledSpinner",
  componentId: "zettle__sc-1wkicws-3"
})(["", ""], _ref8 => {
  let {
    isCentered
  } = _ref8;
  return isCentered && css`
        margin-left: auto;
        margin-right: auto;
        position: absolute;
      ` || css`
      margin-left: ${spacing.xxSmall};
      /* stylelint-disable-next-line @izettle/spacing */
      margin-right: -${getIconOuterMargin};
    `;
});
const ButtonIcon = /*#__PURE__*/styled(Icon).withConfig({
  displayName: "styles__ButtonIcon",
  componentId: "zettle__sc-1wkicws-4"
})(["", " ", ""], _ref9 => {
  let {
    iconPosition = "right"
  } = _ref9;
  return iconPositions[iconPosition];
}, _ref10 => {
  let {
    iconVerticalAlignment = "center"
  } = _ref10;
  return iconVerticalAlignments[iconVerticalAlignment];
});
const ChildrenContainer = /*#__PURE__*/styled.span.withConfig({
  displayName: "styles__ChildrenContainer",
  componentId: "zettle__sc-1wkicws-5"
})(["display:flex;width:100%;flex-wrap:wrap;white-space:normal;hyphens:auto;align-items:center;justify-content:center;text-align:center;", " ", ""], _ref11 => {
  let {
    $horizontalAlignment
  } = _ref11;
  switch ($horizontalAlignment) {
    case "left":
      return css`
          text-align: left;
        `;
    case "right":
      return css`
          text-align: right;
        `;
    default:
      return css`
          text-align: center;
        `;
  }
}, _ref12 => {
  let {
    $loading,
    $emptyWhileLoading
  } = _ref12;
  return $loading && $emptyWhileLoading && css`
      opacity: 0;
    `;
});

export { ButtonIcon, ChildrenContainer, StyledAnchor, StyledButton, StyledLink, StyledSpinner };
//# sourceMappingURL=styles.js.map
